import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Download, Coins } from 'lucide-react';

export interface ReferrerSummary {
  referrer_user_id: string;
  referrer_name: string | null;
  referrer_email: string | null;
  referral_code: string | null;
  clicks: number;
  total_referrals: number;
  registered: number;
  converted: number;
  revenue: number;
  commission_pending: number;
  commission_paid: number;
}

const fmt = (n: number) => Number(n ?? 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });

export default function ReferralPayoutSummary() {
  const [search, setSearch] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['admin_referral_summary'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('admin_referral_summary');
      if (error) throw error;
      return (data ?? []) as ReferrerSummary[];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.referrer_name, r.referrer_email, r.referral_code].some(v => (v ?? '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  const exportCsv = () => {
    const header = ['Referidor', 'Email', 'Código', 'Clics', 'Referidos', 'Registrados', 'Convertidos', 'Ingresos', 'Comisión pendiente', 'Comisión pagada'];
    const lines = filtered.map(r => [
      r.referrer_name ?? '', r.referrer_email ?? '', r.referral_code ?? '',
      r.clicks, r.total_referrals, r.registered, r.converted,
      r.revenue, r.commission_pending, r.commission_paid,
    ]);
    const csv = [header, ...lines]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `liquidacion-referidos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = useMemo(() => ({
    pending: filtered.reduce((a, r) => a + Number(r.commission_pending ?? 0), 0),
    paid: filtered.reduce((a, r) => a + Number(r.commission_paid ?? 0), 0),
  }), [filtered]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="w-4 h-4 text-primary" />
            Liquidación por referidor
          </CardTitle>
          <CardDescription>
            Pendiente: {fmt(totals.pending)} · Pagado: {fmt(totals.paid)}
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="Buscar referidor…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-sm" />
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : (
          <div className="rounded-xl border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referidor</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-right">Clics</TableHead>
                  <TableHead className="text-right">Registrados</TableHead>
                  <TableHead className="text-right">Convertidos</TableHead>
                  <TableHead className="text-right">Ingresos</TableHead>
                  <TableHead className="text-right">Comisión pendiente</TableHead>
                  <TableHead className="text-right">Comisión pagada</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.referrer_user_id}>
                    <TableCell>
                      <p className="font-medium">{r.referrer_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{r.referrer_email}</p>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.referral_code || '—'}</TableCell>
                    <TableCell className="text-right">{fmt(r.clicks)}</TableCell>
                    <TableCell className="text-right">{fmt(r.registered)}</TableCell>
                    <TableCell className="text-right">{fmt(r.converted)}</TableCell>
                    <TableCell className="text-right">{fmt(r.revenue)}</TableCell>
                    <TableCell className="text-right">{fmt(r.commission_pending)}</TableCell>
                    <TableCell className="text-right">{fmt(r.commission_paid)}</TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Sin datos</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
